import { useContext, useEffect, useState } from 'react';

import { useParams } from 'react-router';
import * as Yup from 'yup';

import { Form, Select, Text } from '../../../Components/Form';
import Loading from '../../../Components/Loading/Loading';
import RemoveModal from '../../../Components/Modal/RemoveModal';
import TitleBar from '../../../Components/TitleBar/TitleBar';
import { useApi } from '../../../Hooks/Api';
import { useToast } from '../../../Hooks/Toast';
import Master from '../../../Layouts/Master';
import {
  dateToJalali,
  isValidIranianNationalCode,
  serverErrorHandler,
} from '../../../Utils/Helpers';
import { CompetitionContext } from '../Context/CompetitionContext';

const validationSchema = Yup.object({
  nat_code: Yup.string()
    .required('کد ملی الزامی است')
    .min(10, 'کد ملی باید 10 رقم باشد.')
    .max(10, 'کد ملی باید 10 رقم باشد.')
    .test(
      'nat_code',
      ('nat_code', 'فرمت کد ملی وارد شده اشتباه است'),
      (value) => isValidIranianNationalCode(value),
    ),
});

const CompetitionAgent = () => {
  const context = useContext(CompetitionContext);
  const { id } = useParams();
  const { get, post } = useApi();
  const { error, success } = useToast();
  const [loading, setLoading] = useState(true);
  const [serverErrors, setServerErrors] = useState({});
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [agent, setAgent] = useState();
  const [agents, setAgents] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalRemoveObject, setModalRemoveObject] = useState({});
  const [removeLoading, setRemoveLoading] = useState(false);

  useEffect(() => {
    const getData = async () => {
      await getAgens();
      await getCompetition();
    };
    getData();
  }, [id]);

  const getAgens = async () => {
    setLoading(true);
    await get(
      `/agent?all=true`,
      (res) => {
        context.agents = res.data;
      },
      (err) => {
        setLoading(false);
        error('دریافت اطلاعات با خطا مواجه شد');
      },
    );
  };
  const getCompetition = async () => {
    setLoading(true);
    await get(
      `/competition/${id}`,
      (res) => {
        const agentsList = res?.data?.agents;
        setAgents(agentsList);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        error('دریافت اطلاعات با خطا مواجه شد');
      },
    );
  };

  const searchUser = async (values) => {
    setSearchLoading(true);
    await get(
      `/user/search-no-insurance/${values.nat_code}`,
      (res) => {
        setServerErrors({});
        context.user = res.data;
        setSearchLoading(false);
      },
      (err) => {
        setSearchLoading(false);
        if (err?.status === 422) {
          context.user = {};
          serverErrorHandler(err?.data?.errors, setServerErrors, serverErrors);
          error('لطفا خطاهای فرم را رفع کنید.');
        } else {
          error('دریافت اطلاعات با خطا مواجه شد');
        }
      },
    );
  };

  const [addUserLoading, setAddUserLoading] = useState(false);
  const addUserToList = async () => {
    const agentsCopy = [...agents];
    const agentFind = agentsCopy.find((a) => a.user_id === context.user.id);
    if (agentFind) {
      return error('کاربر قبلا به لیست اضافه شده است');
    }
    const agentInfo = context?.agents?.find((a) => a.id == agent);

    let checkUserRole = true;
    if (agentInfo?.role_name != null) {
      setAddUserLoading(true);
      await get(
        `/user/${context.user?.national_card_id}/check-role/${agentInfo?.role_name}`,
        (res) => {
          setAddUserLoading(false);
        },
        (err) => {
          setAddUserLoading(false);
          if (err?.status === 422) {
            context.user = {};
            serverErrorHandler(
              err?.data?.errors,
              setServerErrors,
              serverErrors,
            );
            error('لطفا خطاهای فرم را رفع کنید.');
          } else {
            error('دریافت اطلاعات با خطا مواجه شد');
          }
          checkUserRole = false;
        },
      );
    }

    if (!checkUserRole) return;

    const user = {
      user_id: context?.user?.id,
      name: context?.user?.name,
      last_name: context?.user?.last_name,
      national_card_id: context.user?.national_card_id,
      province_living: context.user?.province_living,
      agent_id: agentInfo?.id,
      agent_side: agentInfo?.side,
    };

    agentsCopy.push(user);
    setAgents(agentsCopy);
    context.user = {};
  };

  const removeItem = async (userId) => {
    setRemoveLoading(true);
    await setAgents((prevAgents) =>
      prevAgents.filter((a) => a.user_id != userId),
    );
    setModalIsOpen(false);
    setRemoveLoading(false);
  };

  const formSubmit = async () => {
    if (agents.length === 0) {
      return error('لطفا اطلاعات خواسته شده را تکمیل کنید');
    }
    setFormSubmitLoading(true);
    const data = {
      competition_id: id,
      agents: agents?.map((a) => ({
        agent_id: a?.agent_id,
        user_id: a?.user_id,
      })),
    };
    await post(
      '/competition/agent',
      data,
      (res) => {
        success('عوامل اجرا با موفقیت ثبت شد');
        setFormSubmitLoading(false);
      },
      (err) => {
        error('عوامل اجرایی به علت خطا ثبت نشد');
        setFormSubmitLoading(false);
      },
    );
  };

  return loading ? (
    <Loading />
  ) : (
    <Master>
      <TitleBar
        title='عوامل اجرایی مسابقه'
        showBackBtn={true}
        showCreateBtn={false}
        showFilterBtn={false}
        backPath='/competition'
      />
      <Form
        initialValues={{
          nat_code: '',
        }}
        validationSchema={validationSchema}
        defaultActions={false}
        onSubmit={searchUser}
      >
        <div className='row'>
          <div className='col-12 col-md-10'>
            <Text
              name='nat_code'
              title='کد ملی *'
              serverError={serverErrors?.nat_code}
              containerClass='d-flex flex-column justify-content-start align-items-start'
              labelClass='tetx-right mx-2 f-13'
            />
          </div>
          <div className='col-12 col-md-2 mt-4'>
            {searchLoading ? (
              <button className='btn bg-purple w-100 f-13 text-white mt-1'>
                <div
                  className='spinner-border text-white spinner-border-sm mb-1'
                  role='status'
                >
                  <span className='sr-only'></span>
                </div>
              </button>
            ) : (
              <button
                type='submit'
                className='btn bg-purple w-100 f-13 text-white mt-1'
              >
                جستجو
              </button>
            )}
          </div>

          {searchLoading
            ? null
            : context.user?.id !== undefined && (
                <>
                  <div className='col-12'>
                    <div className='d-flex flex-row my-4 border-bottom border-dark'>
                      <h1 className='text-dark f-14 font-weight-bold'>
                        مشخصات کاربر جستجو شده
                      </h1>
                    </div>
                  </div>
                  <div className='col-12 col-md-3 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'>
                        نام و نام خانوادگی :{' '}
                      </span>
                      <span className='text-dark f-14 mx-2'>
                        {context?.user?.name} {context?.user?.last_name}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-3 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'>نام پدر : </span>
                      <span className='text-dark f-14 mx-2'>
                        {context?.user?.father_name}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-3 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'>کدملی : </span>
                      <span className='text-dark f-14 mx-2'>
                        {context?.user?.national_card_id}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-3 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'>
                        شماره همراه :{' '}
                      </span>
                      <span className='text-dark f-14 mx-2'>
                        {context?.user?.mobile}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-3 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'>
                        استان محل زندگی:{' '}
                      </span>
                      <span className='text-dark f-14 mx-2'>
                        {context?.user?.province_living?.name}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-3 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'>تاریخ تولد : </span>
                      <span className='text-dark f-14 mx-2'>
                        {dateToJalali(context?.user?.birthday)}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-6 my-3'>
                    <div className='d-flex flex-row align-items-center'>
                      <span className='text-dark f-14 mx-2'> آدرس : </span>
                      <span className='text-dark f-14 mx-2'>
                        {context?.user?.address}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 my-3'>
                    <div className='d-flex flex-row align-items-center justify-content-end'>
                      <div className='form-group'>
                        <label className='text-dark f-13 d-flex flex-row justify-content-start'>
                          انتخاب سمت
                        </label>
                      </div>
                      <div className='form-group mx-2'>
                        <Select
                          options={context.agents}
                          optionLabel='side'
                          optionValue='id'
                          formikOnChange={false}
                          onChange={(val) => setAgent(val)}
                        />
                      </div>
                      <div className='form-group mx-2'>
                        <button
                          onClick={addUserToList}
                          type='button'
                          className='btn btn-info text-white f-13 mx-2 mb-2 '
                        >
                          افزودن کاربر
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
        </div>
      </Form>
      <div className='row'>
        <div className='col-12'>
          <div className='d-flex flex-row my-4 border-bottom border-dark'>
            <h1 className='text-dark f-14 font-weight-bold'>
              لیست عوامل اجرایی
            </h1>
          </div>
        </div>
        <div className='col-12 overflow-auto'>
          {agents?.length > 0 ? (
            <table
              className='table table-striped my-3 text-left'
              style={{ minWidth: '992px' }}
            >
              <thead>
                <tr className='bg-purple text-white'>
                  <th scope='col'>#</th>
                  <th scope='col'>نام و نام خانوادگی</th>
                  <th scope='col'>کد ملی</th>
                  <th scope='col'>استان محل زندگی</th>
                  <th scope='col'>سمت</th>
                  <th scope='col'>حذف</th>
                </tr>
              </thead>
              <tbody>
                {removeLoading
                  ? null
                  : agents?.map((agent, index) => (
                      <tr key={index}>
                        <th scope='row'>{index + 1}</th>
                        <td>
                          {agent?.name} {agent?.last_name}
                        </td>
                        <td>{agent?.national_card_id}</td>
                        <td>{agent?.province_living?.name}</td>
                        <td>{agent?.agent_side}</td>
                        <td>
                          <i
                            className='fal fa-trash text-danger f-18 c-pointer mt-2'
                            onClick={() => {
                              setModalRemoveObject(agent);
                              setModalIsOpen(true);
                            }}
                          ></i>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          ) : (
            <div className='d-flex flex-row justify-content-center align-items-center my-3'>
              اطلاعاتی جهت نمایش یافت نشد.
            </div>
          )}
        </div>
        <div className='col-12'>
          <div className='my-3 d-flex flex-row justify-content-center'>
            {formSubmitLoading ? (
              <button className='btn bg-persian-green f-12 text-white py-2 px-3'>
                <div
                  className='spinner-border text-white spinner-border-sm mb-1'
                  role='status'
                >
                  <span className='sr-only'></span>
                </div>
              </button>
            ) : (
              <button
                type='button'
                onClick={formSubmit}
                className='btn f-12 bg-persian-green text-white py-2 px-3'
              >
                ذخیره اطلاعات
              </button>
            )}
          </div>
        </div>
      </div>
      <RemoveModal
        id={modalRemoveObject?.user_id}
        modalIsOpen={modalIsOpen}
        removeItem={removeItem}
        removeLoading={removeLoading}
        setModalIsOpen={setModalIsOpen}
        title={`آیا از حذف سمت "${modalRemoveObject?.agent_side}" مطمئن هستید ؟`}
      />
    </Master>
  );
};

export default CompetitionAgent;
